import { useEffect, useState } from "react";

// Each domain serves ONE region in PRODUCTION. Everyone else on that domain
// sees a polite "not available" message after login. Detection is best-effort:
// if we cannot confidently determine the country (common in Android WebView /
// IP-API failure) we FAIL-OPEN and allow login — far better than locking out
// genuine users.
//
// Region gating ONLY applies when REACT_APP_ENV === "production". On staging
// (and any other environment, incl. localhost) the app serves all regions and
// never blocks.
//
// [FIX: geo-domain] The allowed list is now PER-DOMAIN so each brand serves its
// own region (and the Vercel edge middleware redirects mismatches BEFORE this
// popup ever fires — this stays as the safety-net block):
//   • .in  (or localhost:3001) → India only        → US/CA/others get the popup
//   • .ai  (or localhost:3000) → US + Canada only   → India/others get the popup
// We serve exactly TWO brand domains — name BOTH explicitly (full hostnames,
// matched EXACTLY, same as middleware.js) so a look-alike host (e.g.
// app.rexpt.info) can never be misclassified:
//   • app.rexpt.in (or localhost:3001) → India brand   → allow ["IN"]
//   • app.rxpt.ai  (or localhost:3000) → US/CA brand    → allow ["US","CA"]
const IN_HOST = "app.rexpt.in"; // India brand
const AI_HOST = "app.rxpt.ai"; // US / CA brand
function getAllowedCountries() {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const port = typeof window !== "undefined" ? window.location.port : "";
    if (host === IN_HOST || port === "3001") return ["IN"];
    if (host === AI_HOST || port === "3000") return ["US", "CA"];
    // Unknown host (should never happen in production — only these two brands
    // ship). Default to the US/CA rule so we never accidentally over-restrict.
    return ["US", "CA"];
}
const IS_PRODUCTION = process.env.REACT_APP_ENV === "production";
const WHITELIST_IPS =
    process.env.REACT_APP_WHITELIST_IPS
        ?.split(",")
        ?.map(ip => ip?.trim()) || [];

export const useIPRestriction = (info) => {
    const [isBlocked, setIsBlocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [country, setCountry] = useState(null);
  //BLOCKING LOCAL
    useEffect(() => {
        // Location not resolved yet → keep loading, decide later. Never block here.
        if (!info) {
            setLoading(true);
            return;
        }

        try {
            const ip = info.ip || null;
            const detectedCountry = info.country || null;
            setCountry(detectedCountry);

            //  0. Non-production (staging / dev) → serve all regions, never block
            if (!IS_PRODUCTION) {
                // alert("ok")
                setIsBlocked(false);
                return;
            }

            //  1. Whitelisted IP → always allow
            if (ip && WHITELIST_IPS.includes(ip)) {
                setIsBlocked(false);
                return;
            }

            //  2. Country unknown (WebView / API failure) → FAIL-OPEN, allow
            if (!detectedCountry) {
                setIsBlocked(false);
                return;
            }

            //  3. Allow only THIS DOMAIN's served region; block everything else
            //  [FIX: geo-domain] resolve the allowed list per-domain at call time
            //  (.in → ["IN"], .ai → ["US","CA"]).
            const ALLOWED_COUNTRIES = getAllowedCountries();
            setIsBlocked(!ALLOWED_COUNTRIES.includes(detectedCountry));
        } catch (err) {
            console.error("Region restriction error", err);
            setError("Unable to verify location");
            setIsBlocked(false); // fail-open
        } finally {
            setLoading(false);
        }
    }, [info]);

    return { isBlocked, loading, error, country };
};
